import React, { useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { AnimatePresence } from "framer-motion";

import theme from "./components/ui/Theme";
import Header from "./components/ui/Header";
import Footer from "./components/ui/Footer";
import ScrollToTop from "./components/ScrollToTop";
import FloatingBookButton from "./components/FloatingButton";
// import Chatbot from "./Chatbot.tsx";

// All lazy imports must be at the top level, not inside any function/component
const LandingPage = lazy(() => import("./components/LandingPage"));
const Services = lazy(() => import("./components/Services"));
const Software = lazy(() => import("./components/Software"));
const MobileApp = lazy(() => import("./components/MobileApp"));
const GetEstimate = lazy(() => import("./components/pages/GetEstimate"));
const Websites = lazy(() => import("./components/Websites"));
const Revolution = lazy(() => import("./components/Revolution"));
const About = lazy(() => import("./components/About"));
const Contact = lazy(() => import("./components/Contact"));
const Careers = lazy(() => import("./components/pages/Careers"));
const Admin = lazy(() => import("./components/pages/Admin"));
const AIChatbot = lazy(() => import("./components/Estimate"));
const ConfirmEmail = lazy(() => import("./components/pages/ConfirmEmail"));
const Partners = lazy(() => import("./components/Partners"));
const NewsAndEvents = lazy(() => import("./components/pages/NewsAndEvents"));
// const CMSDetails = lazy(() => import("./components/pages/CMSDetails"));
const GCSDetails = lazy(() => import("./components/pages/GCSDetails"));
const OurClient = lazy(() => import("./components/pages/OurClient"));
const CaseStudies = lazy(() =>
  import("./components/pages/CaseStudies/CaseStudies.jsx")
);
const CaseStudyDetail = lazy(() =>
  import("./components/pages/CaseStudies/CaseStudyDetail.jsx")
);
// Animated Routes Component
const AnimatedRoutes = ({ pageProps }) => {
  const location = useLocation();

  return (
    <Suspense
      fallback={
        <div style={{ textAlign: "center", marginTop: "2rem" }}>Loading...</div>
      }
    >
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<LandingPage {...pageProps} />} />
          <Route path="/services" element={<Services {...pageProps} />} />
          <Route path="/software" element={<Software {...pageProps} />} />
          <Route path="/mobileapps" element={<MobileApp {...pageProps} />} />
          <Route path="/websites" element={<Websites {...pageProps} />} />
          <Route path="/evolution" element={<Revolution {...pageProps} />} />
          <Route path="/about" element={<About {...pageProps} />} />
          <Route path="/contact" element={<Contact {...pageProps} />} />
          <Route
            path="/news-events"
            element={<NewsAndEvents {...pageProps} />}
          />
          <Route path="/ai-chatbot" element={<AIChatbot {...pageProps} />} />
          <Route path="/partners" element={<Partners {...pageProps} />} />
          <Route path="/careers" element={<Careers {...pageProps} />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route
            path="/get-estimate"
            element={<GetEstimate {...pageProps} />}
          />
          <Route path="/admin" element={<Admin {...pageProps} />} />
          {/* <Route path="/integrated-software/cms" element={<CMSDetails {...pageProps} />} /> */}
          <Route
            path="/integrated-software/gcs"
            element={<GCSDetails {...pageProps} />}
          />
          <Route path="/our-client" element={<OurClient {...pageProps} />} />
          {/* <Route path="/chatbot" element={<Chatbot />} /> */}
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:id" element={<CaseStudyDetail />} />{" "}
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
};

// Main App Content
const AppContent = () => {
  const [value, setValue] = useState(0);
  const [selected, setSelected] = useState(null);
  const pageProps = { setValue, setSelected };
  const location = useLocation();

  const isAdminPage = location.pathname.startsWith("/admin");

  return (
    <div className="main-container">
      <Header
        value={value}
        setValue={setValue}
        selected={selected}
        setSelected={setSelected}
      />

      <main>
        <AnimatedRoutes pageProps={pageProps} />
      </main>

      {!isAdminPage && <Footer setValue={setValue} setSelected={setSelected} />}

      {/* ✅ ADD THIS HERE */}
      {!isAdminPage && <FloatingBookButton />}
    </div>
  );
};

// App Wrapper
const App = () => (
  <ThemeProvider theme={theme}>
    <BrowserRouter>
      <ScrollToTop />
      <AppContent />
    </BrowserRouter>
  </ThemeProvider>
);

export default App;
